import { X, Star, MapPin, Clock, Zap, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'motion/react';
import { brand } from '@/lib/theme/tokens';
import { unsplash } from '@/lib/utils/img';
import { formatRupees } from '@/lib/utils/format';
import type { UnifiedResult } from '@/features/marketplace/types';

const TYPE_COLOR: Record<string, string> = {
  activity: brand.teal,
  stay:     brand.saffron,
  guide:    brand.chinar,
  vehicle:  brand.cobalt,
  gear:     brand.ok,
  club:     '#9B5DE5',
};
const TYPE_LABEL: Record<string, string> = {
  activity: 'Activity',
  stay:     'Stay',
  guide:    'Guide',
  vehicle:  'Vehicle',
  gear:     'Gear',
  club:     'Club',
};

const DIFF_COLOR: Record<string, string> = {
  Easy:     '#22c55e',
  Moderate: brand.warn,
  Hard:     brand.chinar,
  Expert:   '#ef4444',
};

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid" style={{ gridTemplateColumns: '110px 1fr', alignItems: 'start', gap: '0 8px', padding: '10px 0', borderBottom: '1px solid var(--glass-edge)' }}>
      <span className="text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-mute">{label}</span>
      <span className="text-[13px] text-ink">{children}</span>
    </div>
  );
}

function ItemColumn({ item, onRemove }: { item: UnifiedResult; onRemove: () => void }) {
  const color = TYPE_COLOR[item.type] ?? brand.teal;
  return (
    <div className="flex min-w-0 flex-1 flex-col">
      {/* Photo */}
      <div className="relative aspect-[4/3] overflow-hidden rounded-xl">
        <img
          src={unsplash(item.image, { w: 600 })}
          alt={item.name}
          className="absolute inset-0 h-full w-full object-cover"
        />
        <button
          type="button"
          onClick={onRemove}
          aria-label="Remove from compare"
          className="absolute right-2 top-2 grid h-7 w-7 place-items-center rounded-full border border-white/25 text-white backdrop-blur-md transition hover:bg-white/20"
          style={{ background: 'rgba(0,0,0,0.45)' }}
        >
          <X size={12} />
        </button>
        <span
          className="absolute left-2 top-2 rounded-md px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.1em] text-white backdrop-blur-md"
          style={{ background: `${color}cc` }}
        >
          {TYPE_LABEL[item.type] ?? item.type}
        </span>
      </div>

      {/* Name */}
      <h3 className="mt-3 font-display text-[16px] font-bold leading-snug text-ink line-clamp-2">
        {item.name}
      </h3>

      {/* Rows */}
      <div className="mt-3 flex-1">
        <Row label="Location">
          <span className="flex items-center gap-1"><MapPin size={11} className="shrink-0 text-ink-mute" />{item.location}</span>
        </Row>
        <Row label="Price">
          {item.price != null
            ? <><span className="font-display text-lg font-bold">{formatRupees(item.price)}</span><span className="ml-1 text-[11px] text-ink-mute">/ person</span></>
            : <span className="text-ink-mute">—</span>
          }
        </Row>
        <Row label="Rating">
          {item.rating != null
            ? <span className="flex items-center gap-1"><Star size={12} fill={brand.warn} color={brand.warn} /><span className="font-semibold">{item.rating}</span><span className="text-[11px] text-ink-mute">({item.reviewCount ?? 0})</span></span>
            : <span className="text-ink-mute">—</span>
          }
        </Row>
        {item.duration && (
          <Row label="Duration">
            <span className="flex items-center gap-1"><Clock size={11} className="text-ink-mute" />{item.duration}</span>
          </Row>
        )}
        {item.difficulty && (
          <Row label="Difficulty">
            <span className="flex items-center gap-1.5">
              <Zap size={11} style={{ color: DIFF_COLOR[item.difficulty] ?? brand.teal }} />
              <span style={{ color: DIFF_COLOR[item.difficulty] ?? brand.teal }} className="font-semibold">
                {item.difficulty}
              </span>
            </span>
          </Row>
        )}
      </div>

      {/* CTA */}
      <Link
        to={item.to}
        className="mt-4 flex items-center justify-center gap-2 rounded-xl py-2.5 text-[13px] font-bold text-white transition hover:opacity-90"
        style={{ background: color }}
      >
        View details <ArrowRight size={13} />
      </Link>
    </div>
  );
}

interface Props {
  open: boolean;
  items: UnifiedResult[];
  onRemove: (id: string) => void;
  onClose: () => void;
}

export function CompareModal({ open, items, onRemove, onClose }: Props) {
  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            key="compare-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm"
            onClick={onClose}
          />

          {/* Panel */}
          <motion.div
            key="compare-panel"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 40 }}
            transition={{ type: 'spring', stiffness: 380, damping: 32 }}
            className="fixed inset-x-4 bottom-4 top-16 z-[201] overflow-hidden rounded-3xl md:inset-x-8 md:top-12"
            style={{
              background: 'var(--bg)',
              border: '1px solid var(--glass-edge-hi)',
              boxShadow: '0 32px 80px rgba(0,0,0,0.5)',
            }}
          >
            {/* Header */}
            <div
              className="flex items-center justify-between border-b border-[var(--glass-edge)] px-6 py-4"
            >
              <div>
                <h2 className="font-display text-xl font-bold text-ink">Compare</h2>
                <p className="text-[12px] text-ink-mute">{items.length} item{items.length !== 1 ? 's' : ''} selected</p>
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close comparison"
                className="grid h-9 w-9 place-items-center rounded-full border border-[var(--glass-edge)] text-ink-mute transition hover:text-ink"
              >
                <X size={16} />
              </button>
            </div>

            {/* Columns */}
            <div className="h-[calc(100%-65px)] overflow-y-auto p-5 md:p-6" data-lenis-prevent>
              <div className="flex gap-4 md:gap-6">
                {items.map((item) => (
                  <ItemColumn key={item.id} item={item} onRemove={() => onRemove(item.id)} />
                ))}
                {/* Empty slot hint */}
                {items.length < 3 && (
                  <div
                    className="flex min-w-0 flex-1 flex-col items-center justify-center rounded-2xl border-2 border-dashed py-16 text-center"
                    style={{ borderColor: 'var(--glass-edge-hi)' }}
                  >
                    <span className="text-[13px] text-ink-mute">Add one more item</span>
                    <span className="mt-1 text-[11px] text-ink-dim">tap ⊕ on any card</span>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
